import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
    ArrowUpDown, Bell, CheckCircle2, ChevronDown, ChevronRight, Circle, Flag, Mail, Menu, Plus, Repeat, Star,
    StickyNote, Sun, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup,
    DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { showToast } from "@/lib/toast";
import {
    findCachedTask, useCreateTask, useDeleteTask, useSaveTask, useSyncFlaggedTasks,
    useTaskLists, useTasks, useToggleTask
} from "@/api/hooks";
import type { TaskItem } from "@/api/mailApi";
import { useAppTitle } from "@/lib/useAppTitle";
import {
    DUE_BUCKETS, dueBucket, formatDay, inList, isOverdue, isSmartList, keyToInstant, listName, sortTasks,
    stepProgress, TASK_SORTS, todayKey, useTaskTimeZone, type ListKey, type TaskSort
} from "./taskModel";
import { parseQuickAdd, type ChipKind } from "./quickAdd";
import { TaskDetails } from "./TaskDetails";
import { TASK_DRAG_TYPE, TaskListsPane } from "./TaskListsPane";

const VIEW_KEY = "athavita:tasks:view";

interface ViewPrefs {
    sort: TaskSort;
    groupByDue: boolean;
    showCompleted: boolean;
}

function loadView(): ViewPrefs {
    const fallback: ViewPrefs = { sort: "due", groupByDue: true, showCompleted: true };
    try {
        const raw = localStorage.getItem(VIEW_KEY);
        return raw ? { ...fallback, ...(JSON.parse(raw) as Partial<ViewPrefs>) } : fallback;
    } catch {
        return fallback;
    }
}

/** Today in the user's zone, re-read every minute so My Day empties at local midnight. */
function useToday(tz: string): string {
    const [today, setToday] = useState(() => todayKey(tz));
    useEffect(() => {
        setToday(todayKey(tz));
        const t = window.setInterval(() => setToday(todayKey(tz)), 60_000);
        return () => window.clearInterval(t);
    }, [tz]);
    return today;
}

const isTyping = (el: Element | null) =>
    el instanceof HTMLElement && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

/**
 * Microsoft To Do for Mail: smart lists + user lists on the left, the list in
 * the middle (quick add with local date parsing, grouped by due date), and the
 * selected task's details on the right. Optimistic everywhere, Undo toasts for
 * complete and delete, keyboard j/k · Enter · Space · Del.
 */
export function TasksPage() {
    const [params, setParams] = useSearchParams();
    const qc = useQueryClient();
    const tz = useTaskTimeZone();
    const today = useToday(tz);
    const tasksQ = useTasks();
    const listsQ = useTaskLists();
    const create = useCreateTask();
    const save = useSaveTask();
    const toggle = useToggleTask();
    const del = useDeleteTask();
    const syncFlagged = useSyncFlaggedTasks();

    const title = useAppTitle("Athavita Tasks");
    const mailTitle = useAppTitle("Athavita Mail");
    useEffect(() => {
        document.title = title;
        return () => { document.title = mailTitle; };
    }, [title, mailTitle]);

    const lists = useMemo(() => listsQ.data ?? [], [listsQ.data]);
    const [hidden, setHidden] = useState<Set<string>>(() => new Set());
    const tasks = useMemo(() => (tasksQ.data ?? []).filter((t) => !hidden.has(t.id)), [tasksQ.data, hidden]);

    // ── URL state: ?list=<smart id | list id>&task=<id> ──
    const selectedId = params.get("task");
    const rawList = params.get("list");
    const selectedTask = selectedId ? tasks.find((t) => t.id === selectedId) ?? null : null;
    const listKey: ListKey = (() => {
        if (rawList && (isSmartList(rawList) || lists.some((l) => l.id === rawList) || !listsQ.data)) return rawList;
        if (!rawList && selectedTask) return selectedTask.listId ?? (selectedTask.source === "flagged" ? "flagged" : "tasks");
        return rawList ? "tasks" : "myday";
    })();
    const setList = (key: ListKey) => { setParams({ list: key }); setListsOpen(false); };
    const select = useCallback((id: string | null) => {
        setParams((p) => {
            const next = new URLSearchParams(p);
            if (!next.get("list")) next.set("list", listKey);
            if (id) next.set("task", id);
            else next.delete("task");
            return next;
        });
    }, [setParams, listKey]);

    // ── Flagged email mirror: on open, every 2 minutes, and on focus ──
    const syncRef = useRef(syncFlagged.mutate);
    syncRef.current = syncFlagged.mutate;
    useEffect(() => {
        const run = () => syncRef.current();
        run();
        const t = window.setInterval(run, 120_000);
        window.addEventListener("focus", run);
        return () => { window.clearInterval(t); window.removeEventListener("focus", run); };
    }, []);

    // ── View prefs ──
    const [view, setView] = useState<ViewPrefs>(loadView);
    const updateView = (p: Partial<ViewPrefs>) => setView((v) => {
        const next = { ...v, ...p };
        try { localStorage.setItem(VIEW_KEY, JSON.stringify(next)); } catch { /* per-viewer convenience only */ }
        return next;
    });
    const [completedOpen, setCompletedOpen] = useState(true);
    const [listsOpen, setListsOpen] = useState(false);

    const visible = useMemo(() => tasks.filter((t) => inList(t, listKey, today)), [tasks, listKey, today]);
    const open = useMemo(() => sortTasks(visible.filter((t) => !t.isCompleted), view.sort), [visible, view.sort]);
    const done = useMemo(
        () => visible.filter((t) => t.isCompleted).sort((a, b) => (b.completedAt ?? "").localeCompare(a.completedAt ?? "")),
        [visible]
    );
    const groups = useMemo(() => view.groupByDue
        ? DUE_BUCKETS.map((b) => ({ id: b.id, label: b.label, items: open.filter((t) => dueBucket(t, tz, today) === b.id) }))
            .filter((g) => g.items.length > 0)
        : [{ id: "all", label: "", items: open }], [open, view.groupByDue, tz, today]);
    const showDone = view.showCompleted && done.length > 0;
    const flat = useMemo(
        () => [...groups.flatMap((g) => g.items), ...(showDone && completedOpen ? done : [])],
        [groups, showDone, completedOpen, done]
    );

    // ── Actions ──
    const complete = useCallback((t: TaskItem) => {
        const wasDone = t.isCompleted;
        toggle.mutate({ id: t.id, complete: !wasDone }, {
            onSuccess: (res) => {
                if (wasDone) return;
                showToast(`Completed “${t.title}”${res.next ? " — next one scheduled" : ""}`, {
                    action: {
                        label: "Undo",
                        onClick: () => {
                            toggle.mutate({ id: t.id, complete: false });
                            if (res.next) del.mutate(res.next.id);
                        }
                    }
                });
            },
            onError: () => showToast("Couldn't update the task")
        });
    }, [toggle, del]);

    // Delete is deferred for the Undo window, then sent.
    const pending = useRef(new Map<string, ReturnType<typeof setTimeout>>());
    const delRef = useRef(del.mutate);
    delRef.current = del.mutate;
    useEffect(() => () => {
        for (const [id, timer] of pending.current) { clearTimeout(timer); delRef.current(id); }
        pending.current.clear();
    }, []);
    const remove = useCallback((t: TaskItem) => {
        setHidden((h) => new Set(h).add(t.id));
        if (selectedId === t.id) select(null);
        const unhide = () => setHidden((h) => { const n = new Set(h); n.delete(t.id); return n; });
        const timer = setTimeout(() => {
            pending.current.delete(t.id);
            delRef.current(t.id, { onSettled: unhide });
        }, 5000);
        pending.current.set(t.id, timer);
        showToast(`Deleted “${t.title}”`, {
            durationMs: 5000,
            action: {
                label: "Undo",
                onClick: () => { clearTimeout(timer); pending.current.delete(t.id); unhide(); }
            }
        });
    }, [selectedId, select]);

    const dropOnList = (taskId: string, key: ListKey) => {
        const t = findCachedTask(qc, taskId);
        if (!t || key === "flagged") return;
        const next: TaskItem =
            key === "important" ? { ...t, isImportant: true }
            : key === "myday" ? { ...t, myDayDate: today }
            : key === "planned" ? { ...t, dueAt: t.dueAt ?? keyToInstant(today, tz).toISOString() }
            : key === "tasks" ? { ...t, listId: null }
            : { ...t, listId: key };
        save.mutate(next);
        showToast(`Moved to ${listName(key, lists)}`);
    };

    // ── Quick add ──
    const [draft, setDraft] = useState("");
    const [ignore, setIgnore] = useState<Set<ChipKind>>(() => new Set());
    const parsed = useMemo(() => parseQuickAdd(draft, tz, ignore), [draft, tz, ignore]);
    const addTask = (e: FormEvent) => {
        e.preventDefault();
        if (!draft.trim()) return;
        create.mutate({
            title: parsed.title.slice(0, 512),
            dueAt: parsed.dueAt ?? (listKey === "planned" ? keyToInstant(today, tz).toISOString() : null),
            reminderAt: parsed.reminderAt,
            recurrence: parsed.recurrence,
            isImportant: parsed.isImportant || listKey === "important",
            myDayDate: listKey === "myday" ? today : null,
            listId: isSmartList(listKey) ? null : listKey
        }, { onError: () => showToast("Couldn't add the task") });
        setDraft("");
        setIgnore(new Set());
    };

    // ── Keyboard: j/k (↓/↑) move, Enter opens, Space completes, Del deletes, Esc closes ──
    const [cursor, setCursor] = useState<string | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTyping(document.activeElement)) return;
            if (document.querySelector("[role=dialog],[role=menu]")) return;
            const current = cursor ?? selectedId;
            const idx = current ? flat.findIndex((t) => t.id === current) : -1;
            const target = idx >= 0 ? flat[idx] : undefined;
            switch (e.key) {
                case "j": case "ArrowDown": {
                    const n = flat[Math.min(flat.length - 1, idx + 1)];
                    if (n) { setCursor(n.id); e.preventDefault(); }
                    break;
                }
                case "k": case "ArrowUp": {
                    const n = flat[Math.max(0, idx - 1)];
                    if (n) { setCursor(n.id); e.preventDefault(); }
                    break;
                }
                case "Enter":
                    if (target) { select(target.id); e.preventDefault(); }
                    break;
                case " ":
                    if (target) { complete(target); e.preventDefault(); }
                    break;
                case "Delete": case "Backspace":
                    if (target) { remove(target); e.preventDefault(); }
                    break;
                case "Escape":
                    if (selectedId) { select(null); e.preventDefault(); }
                    break;
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [cursor, selectedId, flat, select, complete, remove]);
    useEffect(() => {
        if (cursor) listRef.current?.querySelector(`[data-task-id="${CSS.escape(cursor)}"]`)?.scrollIntoView({ block: "nearest" });
    }, [cursor]);

    const header = listName(listKey, lists);
    const userList = lists.find((l) => l.id === listKey);
    const rowProps = {
        tz, today, lists,
        showList: isSmartList(listKey) && listKey !== "tasks",
        showMyDay: listKey !== "myday",
        onToggle: complete,
        onStar: (t: TaskItem) => save.mutate({ ...t, isImportant: !t.isImportant }),
        onOpen: (t: TaskItem) => { setCursor(t.id); select(t.id); }
    };

    return (
        <section className="relative flex h-full min-w-0 flex-1 bg-muted/30" aria-label="Tasks">
            {/* Lists: a column from md up, a drawer below. */}
            {listsOpen ? <div className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setListsOpen(false)} aria-hidden /> : null}
            <TaskListsPane
                selected={listKey}
                onSelect={setList}
                tasks={tasksQ.data ?? []}
                lists={lists}
                today={today}
                onDropTask={dropOnList}
                className={cn(
                    "w-60 shrink-0 border-r bg-card",
                    listsOpen ? "fixed inset-y-0 left-0 z-40 w-64 shadow-xl md:static md:z-auto md:shadow-none" : "hidden md:flex"
                )}
            />

            <div className="flex min-w-0 flex-1 flex-col">
                <header className="flex h-12 shrink-0 items-center gap-2 border-b bg-card px-3">
                    <Button variant="ghost" size="icon" className="h-8 w-8 md:hidden" onClick={() => setListsOpen(true)} aria-label="Show lists">
                        <Menu className="h-4 w-4" />
                    </Button>
                    {userList?.emoji ? <span aria-hidden>{userList.emoji}</span> : null}
                    <h1 className="truncate text-base font-semibold" style={{ color: userList?.color ?? undefined }}>{header}</h1>
                    {listKey === "myday" ? (
                        <span className="hidden text-xs text-muted-foreground sm:inline">
                            {keyToInstant(today, tz, 12).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", timeZone: tz })}
                        </span>
                    ) : null}
                    <span className="ml-auto text-xs text-muted-foreground">{open.length} open</span>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="gap-1" aria-label="View options">
                                <ArrowUpDown className="h-4 w-4" /> <span className="hidden sm:inline">View</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                            <DropdownMenuRadioGroup value={view.sort} onValueChange={(v) => updateView({ sort: v as TaskSort })}>
                                {TASK_SORTS.map((s) => <DropdownMenuRadioItem key={s.id} value={s.id}>{s.label}</DropdownMenuRadioItem>)}
                            </DropdownMenuRadioGroup>
                            <DropdownMenuSeparator />
                            <DropdownMenuCheckboxItem checked={view.groupByDue} onCheckedChange={(v) => updateView({ groupByDue: Boolean(v) })}>
                                Group by due date
                            </DropdownMenuCheckboxItem>
                            <DropdownMenuCheckboxItem checked={view.showCompleted} onCheckedChange={(v) => updateView({ showCompleted: Boolean(v) })}>
                                Show completed
                            </DropdownMenuCheckboxItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </header>

                {listKey === "flagged" ? (
                    <p className="shrink-0 border-b bg-card px-4 py-2 text-xs text-muted-foreground">
                        Messages you flag in Mail appear here. Completing one unflags the message.
                        {syncFlagged.isPending ? " Syncing…" : ""}
                    </p>
                ) : (
                    <form onSubmit={addTask} className="shrink-0 border-b bg-card px-3 py-2">
                        <div className="flex items-center gap-2 rounded-md border bg-background px-2 focus-within:ring-2 focus-within:ring-ring">
                            <Plus className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                            <input
                                value={draft}
                                onChange={(e) => { setDraft(e.target.value); if (!e.target.value) setIgnore(new Set()); }}
                                placeholder='Add a task — try "Call bank tomorrow 5pm !"'
                                aria-label="Add a task"
                                className="h-9 min-w-0 flex-1 bg-transparent text-sm outline-none"
                            />
                            <Button type="submit" size="sm" disabled={!draft.trim()}>Add</Button>
                        </div>
                        {draft.trim() && parsed.chips.length > 0 ? (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5" aria-label="Recognised details">
                                <span className="text-xs text-muted-foreground">“{parsed.title}”</span>
                                {parsed.chips.map((c) => (
                                    <span key={c.kind} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                                        {c.label}
                                        <button
                                            type="button"
                                            aria-label={`Don't use: ${c.label}`}
                                            onClick={() => setIgnore((s) => new Set(s).add(c.kind))}
                                            className="rounded-full hover:bg-primary/20"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        ) : null}
                    </form>
                )}

                <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-2 py-2 sm:px-4">
                    {tasksQ.isPending ? (
                        <p className="py-8 text-center text-sm text-muted-foreground">Loading tasks…</p>
                    ) : tasksQ.isError && !tasksQ.data ? (
                        <p className="py-8 text-center text-sm text-destructive">Couldn't load tasks.</p>
                    ) : open.length === 0 && !showDone ? (
                        <p className="py-10 text-center text-sm text-muted-foreground">
                            {listKey === "myday" ? "Focus on your day — add tasks, or star them in other lists into My Day."
                                : listKey === "flagged" ? "No flagged email."
                                : "No tasks here yet."}
                        </p>
                    ) : null}
                    {groups.map((g) => g.items.length === 0 ? null : (
                        <div key={g.id} className="mb-3">
                            {g.label ? (
                                <h2 className={cn("px-1 pb-1 text-xs font-semibold", g.id === "overdue" ? "text-destructive" : "text-muted-foreground")}>
                                    {g.label} <span className="font-normal">{g.items.length}</span>
                                </h2>
                            ) : null}
                            <ul role="list" className="space-y-1">
                                {g.items.map((t) => (
                                    <TaskRow key={t.id} task={t} active={selectedId === t.id} focused={cursor === t.id} {...rowProps} />
                                ))}
                            </ul>
                        </div>
                    ))}
                    {showDone ? (
                        <div className="mb-3">
                            <button
                                type="button"
                                onClick={() => setCompletedOpen((o) => !o)}
                                aria-expanded={completedOpen}
                                className="flex items-center gap-1 rounded px-1 pb-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
                            >
                                {completedOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                Completed <span className="font-normal">{done.length}</span>
                            </button>
                            {completedOpen ? (
                                <ul role="list" className="space-y-1">
                                    {done.map((t) => (
                                        <TaskRow key={t.id} task={t} active={selectedId === t.id} focused={cursor === t.id} {...rowProps} />
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                    ) : null}
                    <p className="hidden px-1 pt-2 text-[11px] text-muted-foreground lg:block">
                        j / k to move · Enter to open · Space to complete · Del to delete
                    </p>
                </div>
            </div>

            {selectedTask ? (
                <>
                    <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => select(null)} aria-hidden />
                    <TaskDetails
                        task={selectedTask}
                        lists={lists}
                        tz={tz}
                        today={today}
                        onClose={() => select(null)}
                        onSave={(t) => save.mutate(t, { onError: () => showToast("Couldn't save the task") })}
                        onToggle={complete}
                        onDelete={remove}
                        className="fixed inset-y-0 right-0 z-40 w-full shadow-xl sm:w-96 lg:static lg:z-auto lg:w-96 lg:shrink-0 lg:shadow-none"
                    />
                </>
            ) : null}
        </section>
    );
}

// Kept out of the page so each row re-renders only with its own task.
function TaskRow({
    task, active, focused, tz, today, lists, showList, showMyDay, onToggle, onStar, onOpen
}: {
    task: TaskItem;
    active: boolean;
    focused: boolean;
    tz: string;
    today: string;
    lists: { id: string; name: string }[];
    showList: boolean;
    showMyDay: boolean;
    onToggle: (t: TaskItem) => void;
    onStar: (t: TaskItem) => void;
    onOpen: (t: TaskItem) => void;
}) {
    const progress = stepProgress(task);
    const overdue = isOverdue(task, tz, today);
    const meta: React.ReactNode[] = [];
    if (showList) {
        meta.push(<span key="list">{task.source === "flagged" ? "Flagged email" : task.listId ? lists.find((l) => l.id === task.listId)?.name ?? "List" : "Tasks"}</span>);
    }
    if (showMyDay && task.myDayDate === today) meta.push(<span key="md" className="inline-flex items-center gap-0.5"><Sun className="h-3 w-3" />My Day</span>);
    if (progress) meta.push(<span key="steps">{progress}</span>);
    if (task.dueAt) {
        meta.push(
            <span key="due" className={cn(overdue ? "font-medium text-destructive" : dueBucket(task, tz, today) === "today" && "text-primary")}>
                {overdue ? "Overdue, " : ""}{formatDay(task.dueAt, tz, today)}
            </span>
        );
    }
    if (task.reminderAt && !task.isCompleted) meta.push(<Bell key="rem" className="h-3 w-3" aria-label="Reminder" />);
    if (task.recurrence) meta.push(<Repeat key="rep" className="h-3 w-3" aria-label="Repeats" />);
    if (task.notes) meta.push(<StickyNote key="notes" className="h-3 w-3" aria-label="Has notes" />);
    if (task.relatedMessageId) {
        meta.push(task.source === "flagged"
            ? <Flag key="mail" className="h-3 w-3 text-red-500" aria-label="Flagged email" />
            : <Mail key="mail" className="h-3 w-3" aria-label="Linked email" />);
    }

    return (
        <li
            data-task-id={task.id}
            draggable
            onDragStart={(e) => { e.dataTransfer.setData(TASK_DRAG_TYPE, task.id); e.dataTransfer.effectAllowed = "move"; }}
            className={cn(
                "group flex items-center gap-3 rounded-md border bg-card px-3 py-2 shadow-sm transition-colors hover:bg-accent/50",
                active && "border-primary/60 bg-primary/5",
                focused && "ring-2 ring-primary/60"
            )}
        >
            <button
                type="button"
                onClick={() => onToggle(task)}
                aria-label={task.isCompleted ? `Mark “${task.title}” incomplete` : `Complete “${task.title}”`}
                className="shrink-0 text-muted-foreground hover:text-primary"
            >
                {task.isCompleted ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <Circle className="h-5 w-5" />}
            </button>
            <button type="button" onClick={() => onOpen(task)} className="min-w-0 flex-1 text-left" aria-current={active ? "true" : undefined}>
                <span className={cn("block truncate text-sm", task.isCompleted && "text-muted-foreground line-through")}>{task.title}</span>
                {meta.length > 0 ? (
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                        {meta.map((m, i) => (
                            <span key={i} className="inline-flex items-center gap-2">
                                {i > 0 ? <span aria-hidden>·</span> : null}
                                {m}
                            </span>
                        ))}
                    </span>
                ) : null}
            </button>
            <button
                type="button"
                onClick={() => onStar(task)}
                aria-label={task.isImportant ? "Remove importance" : "Mark as important"}
                aria-pressed={task.isImportant}
                className="shrink-0 text-muted-foreground hover:text-primary"
            >
                <Star className={cn("h-4 w-4", task.isImportant && "fill-primary text-primary")} />
            </button>
        </li>
    );
}
